import type { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { ProgressSteps } from "@/components/ui/progress-steps";
import { cn } from "@/lib/utils";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";

export const PRODUCT_FORM_STEPS = [
  { id: "basics", label: "Basic details", hint: "Photos, name, price, and category" },
  { id: "variants", label: "Options", hint: "Variants, SKU, and stock" },
  { id: "details", label: "More details", hint: "Shipping, SEO, and extras" },
] as const;

type ProductFormShellProps = {
  title: string;
  badges?: ReactNode;
  actions?: ReactNode;
  step: number;
  onStepChange: (step: number) => void;
  onBack: () => void;
  onSave: () => void;
  saveLabel: string;
  saving?: boolean;
  canContinue?: () => boolean | Promise<boolean>;
  children: ReactNode;
};

export function ProductFormShell({
  title,
  badges,
  actions,
  step,
  onStepChange,
  onBack,
  onSave,
  saveLabel,
  saving,
  canContinue,
  children,
}: ProductFormShellProps) {
  const last = PRODUCT_FORM_STEPS.length - 1;
  const current = PRODUCT_FORM_STEPS[step] ?? PRODUCT_FORM_STEPS[0];

  const goNext = async () => {
    if (canContinue) {
      const ok = await canContinue();
      if (!ok) return;
    }
    onStepChange(Math.min(step + 1, last));
  };

  return (
    <div className="relative w-full min-w-0 pb-32">
      <div className="mb-6 flex items-start justify-between gap-3 sm:mb-8">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex size-10 shrink-0 items-center justify-center rounded-full border border-primary/15 text-primary transition-colors hover:bg-[#f6f4ef]"
          aria-label="Back to products"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        {actions ? (
          <div className="flex min-w-0 flex-wrap justify-end gap-2">{actions}</div>
        ) : (
          <span className="size-10" aria-hidden />
        )}
      </div>

      <ProgressSteps
        step={step + 1}
        total={PRODUCT_FORM_STEPS.length}
        className="mb-6 sm:mb-8"
        onStepSelect={(next) => onStepChange(next - 1)}
      />

      <div className="mb-6 sm:mb-8">
        <p className="mb-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-primary/45">
          {title}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="font-heading text-[1.75rem] leading-[1.05] tracking-tight text-primary sm:text-3xl">
            {current.label}
          </h1>
          {badges}
        </div>
        <p className="mt-2 max-w-xl text-sm font-medium leading-relaxed text-primary/65 sm:text-base">
          {current.hint}
        </p>
      </div>

      <div className="min-w-0">{children}</div>

      <div className="fixed inset-x-0 bottom-0 z-20 border-t border-[#EBEBEB] bg-white/95 px-4 py-3 backdrop-blur md:static md:mt-10 md:border-0 md:bg-transparent md:p-0 md:backdrop-blur-none">
        <div className="flex w-full max-w-xl gap-3 pb-[env(safe-area-inset-bottom)] md:max-w-none md:justify-end md:pb-0">
          {step > 0 ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => onStepChange(Math.max(step - 1, 0))}
              className="h-12 flex-1 rounded-full border-primary/15 font-semibold shadow-none hover:bg-[#f6f4ef] md:flex-none md:px-8"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
          ) : null}
          {step < last ? (
            <Button
              type="button"
              onClick={goNext}
              className="h-12 flex-1 rounded-full font-semibold shadow-none md:flex-none md:min-w-[140px] md:px-8"
            >
              Next
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          ) : (
            <Button
              type="button"
              onClick={onSave}
              disabled={saving}
              className="h-12 flex-1 rounded-full font-semibold shadow-none md:flex-none md:min-w-[160px] md:px-8"
            >
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {saving ? "Saving…" : saveLabel}
            </Button>
          )}
        </div>
        {step < last ? (
          <button
            type="button"
            onClick={onSave}
            disabled={saving}
            className={cn(
              "mx-auto mt-2 block text-center text-sm font-semibold text-primary/70 hover:text-primary md:mr-0 md:ml-auto",
              saving && "pointer-events-none opacity-50",
            )}
          >
            {saving ? "Saving…" : "Save without finishing"}
          </button>
        ) : null}
      </div>
    </div>
  );
}
